import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { api } from '../utils/api';
import { Plus, Users, Pencil, Trash2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

export const Eventos = () => {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    fecha_inicio: '',
    fecha_fin: '',
    estado: 'Planificado'
  });

  const fetchCustomers = React.useCallback(async () => {
    try {
      const response = await api.getCustomers();
      setCustomers(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') console.error('Error fetching customers:', error);
    }
  }, []);

  // Carga inicial
  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  // POLLING DESACTIVADO - causaba recargas visuales y pérdida de foco
  // useEffect(() => {
  //   const pollingInterval = setInterval(() => {
  //     fetchCustomers();
  //   }, 5000);
  //   return () => clearInterval(pollingInterval);
  // }, [fetchCustomers]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCustomer) {
        await api.updateCustomer(editingCustomer.id, formData);
        toast.success('Evento actualizado');
      } else {
        await api.createCustomer(formData);
        toast.success('Evento creado');
      }
      setDialogOpen(false);
      resetForm();
      fetchCustomers();
    } catch (error) {
      const errorMessage = error.response?.data?.detail 
        ? (typeof error.response.data.detail === 'string' 
           ? error.response.data.detail 
           : 'Error de validación en los datos del cliente')
        : 'Error al guardar cliente';
      toast.error(errorMessage);
    }
  };

  const handleEdit = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      name: customer.name,
      address: customer.address || '',
      fecha_inicio: customer.fecha_inicio || '',
      fecha_fin: customer.fecha_fin || '',
      estado: customer.estado || 'Planificado'
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este evento?')) return;
    try {
      await api.deleteCustomer(id);
      toast.success('Evento eliminado');
      fetchCustomers();
    } catch (error) {
      const errorMessage = error.response?.data?.detail 
        ? (typeof error.response.data.detail === 'string' 
           ? error.response.data.detail 
           : 'Error al eliminar evento')
        : 'Error al eliminar evento';
      toast.error(errorMessage);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      address: '',
      fecha_inicio: '',
      fecha_fin: '',
      estado: 'Planificado'
    });
    setEditingCustomer(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 dark:text-white">Eventos</h1>
          <p className="text-base text-slate-600 dark:text-slate-300 mt-2">Gestión de eventos</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button variant="primary" size="default" data-testid="add-customer-button">
              <Plus className="h-5 w-5 mr-2" />
              Nuevo Evento
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingCustomer ? 'Editar Evento' : 'Nuevo Evento'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Nombre del Evento *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="Ej: Congreso Internacional de Tecnología"
                  required
                  data-testid="customer-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="address">Ubicación / Dirección</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  placeholder="Ciudad, País o dirección completa"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="fecha_inicio">Fecha Inicio</Label>
                  <Input
                    id="fecha_inicio"
                    type="date"
                    value={formData.fecha_inicio}
                    onChange={(e) => setFormData({...formData, fecha_inicio: e.target.value})}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="fecha_fin">Fecha Fin</Label>
                  <Input
                    id="fecha_fin"
                    type="date"
                    value={formData.fecha_fin}
                    onChange={(e) => setFormData({...formData, fecha_fin: e.target.value})}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="estado">Estado *</Label>
                <select
                  id="estado"
                  value={formData.estado}
                  onChange={(e) => setFormData({...formData, estado: e.target.value})}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  required
                >
                  <option value="Planificado">Planificado</option>
                  <option value="En curso">En curso</option>
                  <option value="Cerrado">Cerrado</option>
                </select>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <Button 
                  type="button" 
                  variant="secondary" 
                  size="default"
                  onClick={() => { setDialogOpen(false); resetForm(); }}
                >
                  Cancelar
                </Button>
                <Button 
                  type="submit" 
                  variant="success"
                  size="default"
                  data-testid="save-customer-button"
                >
                  {editingCustomer ? 'Actualizar' : 'Crear'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
        <CardHeader>
          <CardTitle>Lista de Eventos</CardTitle>
        </CardHeader>
        <CardContent>
          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="uppercase tracking-wider text-xs">Nombre del Evento</TableHead>
                  <TableHead className="uppercase tracking-wider text-xs">Ubicación</TableHead>
                  <TableHead className="uppercase tracking-wider text-xs">Estado</TableHead>
                  <TableHead className="uppercase tracking-wider text-xs">Fecha Inicio</TableHead>
                  <TableHead className="uppercase tracking-wider text-xs">Fecha Fin</TableHead>
                  <TableHead className="uppercase tracking-wider text-xs">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {customers.map((customer) => (
                  <TableRow key={customer.id} className="hover:bg-slate-50 dark:hover:bg-slate-900">
                    <TableCell className="font-medium">{customer.name}</TableCell>
                    <TableCell>{customer.address || '-'}</TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        customer.estado === 'En curso' ? 'bg-green-100 text-green-800' :
                        customer.estado === 'Cerrado' ? 'bg-gray-100 text-gray-800' :
                        'bg-blue-100 text-blue-800'
                      }`}>
                        {customer.estado || 'Planificado'}
                      </span>
                    </TableCell>
                    <TableCell>{customer.fecha_inicio || '-'}</TableCell>
                    <TableCell>{customer.fecha_fin || '-'}</TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button 
                          variant="default" 
                          size="icon-sm" 
                          onClick={() => navigate(`/events/${customer.id}`)}
                          className="hover:bg-indigo-600"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="secondary" 
                          size="icon-sm" 
                          onClick={() => handleEdit(customer)}
                          data-testid={`edit-customer-${customer.id}`}
                          className="hover:bg-blue-100 hover:text-blue-700"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="danger" 
                          size="icon-sm" 
                          onClick={() => handleDelete(customer.id)}
                          data-testid={`delete-customer-${customer.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden space-y-3">
            {customers.map((customer) => (
              <div key={customer.id} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4 shadow-sm">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg text-slate-900 dark:text-white mb-1">{customer.name}</h3>
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      customer.estado === 'En curso' ? 'bg-green-100 text-green-800' :
                      customer.estado === 'Cerrado' ? 'bg-gray-100 text-gray-800' :
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {customer.estado || 'Planificado'}
                    </span>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="default" 
                      size="icon-sm" 
                      onClick={() => navigate(`/events/${customer.id}`)}
                      className="hover:bg-indigo-600"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="secondary" 
                      size="icon-sm" 
                      onClick={() => handleEdit(customer)}
                      className="hover:bg-blue-100 hover:text-blue-700"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="danger" 
                      size="icon-sm" 
                      onClick={() => handleDelete(customer.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div className="space-y-1 text-sm">
                  {customer.address && (
                    <div className="flex items-center gap-2">
                      <span className="text-slate-500 dark:text-slate-400">Ubicación:</span>
                      <span className="text-slate-900 dark:text-white">{customer.address}</span>
                    </div>
                  )}
                  {customer.fecha_inicio && (
                    <div className="flex items-center gap-2">
                      <span className="text-slate-500 dark:text-slate-400">Inicio:</span>
                      <span className="text-slate-900 dark:text-white">{customer.fecha_inicio}</span>
                    </div>
                  )}
                  {customer.fecha_fin && (
                    <div className="flex items-center gap-2">
                      <span className="text-slate-500 dark:text-slate-400">Fin:</span>
                      <span className="text-slate-900 dark:text-white">{customer.fecha_fin}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
          {customers.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              No hay eventos registrados
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
